import React, { useState, useEffect } from 'react';
import ProjectList from './components/ProjectList';
import CreateProject from './components/CreateProject';
import SubmissionsGrading from './components/SubmissionsGrading';
import ViewProject from './components/ViewProject';
import useGroupProjectManagementStore from '@/stores/SuperAdminStore/GroupProjectManagementStore';
import { Project } from '@/services/SuperAdminServices/GroupProjectManagementService';
import './GroupProjectManagement.css';

type TabType = 'list' | 'create' | 'submissions' | 'view';

const GroupProjectManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('list');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const {
    projects,
    submissions,
    fetchProjects,
    fetchSubmissions,
  } = useGroupProjectManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'list' && !projects) {
      fetchProjects();
    }
  }, [activeTab, projects, fetchProjects]);

  // Submissions are fetched when user selects a specific project in the SubmissionsGrading component
  // Not fetched automatically on tab change

  const tabs = [
    { id: 'list' as TabType, label: 'Project List' },
    { id: 'create' as TabType, label: 'Create Project' },
    { id: 'submissions' as TabType, label: 'Submissions & Grading' },
  ];

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setIsEditMode(false);
    setActiveTab('view');
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setIsEditMode(true);
    setActiveTab('create');
  };

  const handleEditFromView = () => {
    setIsEditMode(true);
    setActiveTab('create');
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setIsEditMode(false);
    setActiveTab('list');
  };

  // Force a clean "create" entry — any leftover edit state from a prior
  // session (e.g. after Publish) would otherwise prefill the form again.
  const goToCreate = () => {
    setIsEditMode(false);
    setSelectedProject(null);
    setActiveTab('create');
  };

  const handleTabClick = (next: TabType) => {
    if (next === 'create') {
      goToCreate();
      return;
    }
    setActiveTab(next);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return (
          <ProjectList
            onCreateClick={goToCreate}
            onViewProject={handleViewProject}
            onEditProject={handleEditProject}
          />
        );
      case 'create':
        return (
          <CreateProject
            key={isEditMode && selectedProject ? `edit-${selectedProject.id}` : 'new'}
            project={isEditMode ? selectedProject : null}
            onSuccess={handleBackToList}
            onCancel={handleBackToList}
          />
        );
      case 'view':
        return selectedProject ? (
          <ViewProject
            project={selectedProject}
            onEdit={handleEditFromView}
            onBack={handleBackToList}
          />
        ) : (
          <ProjectList
            onCreateClick={goToCreate}
            onViewProject={handleViewProject}
            onEditProject={handleEditProject}
          />
        );
      case 'submissions':
        return <SubmissionsGrading />;
      default:
        return (
          <ProjectList
            onCreateClick={goToCreate}
            onViewProject={handleViewProject}
            onEditProject={handleEditProject}
          />
        );
    }
  };

  return (
    <div className="admin-group-project-management">
      <div className="admin-group-project-header">
        <div className="admin-group-project-breadcrumb">
          <span>Content Management</span>
          <span className="admin-group-project-separator">›</span>
          <span>Group Projects</span>
        </div>
        <div className="admin-group-project-header-content">
          <div>
            <h1>Group Projects</h1>
            <p>Create and manage collaborative learning projects</p>
          </div>
          {activeTab === 'list' && (
            <button
              className="admin-group-project-create-btn"
              onClick={goToCreate}
            >
              + Create Project
            </button>
          )}
        </div>
      </div>

      <div className="admin-group-project-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-group-project-tab ${activeTab === tab.id ? 'admin-group-project-tab-active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-group-project-content">{renderTabContent()}</div>
    </div>
  );
};

export default GroupProjectManagement;
